import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  BarChart3,
  ChevronRight,
  LogIn,
  LogOut,
  Menu,
  MessageCircle,
  ShieldCheck,
  X,
} from "lucide-react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";

import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

import { auth, db } from "../firebase/firebase";

const navigationLinks = [
  {
    label: "Home",
    path: "/",
  },
  {
    label: "How It Works",
    path: "/how-it-works",
  },
  {
    label: "Tasks",
    path: "/tasks",
  },
  {
    label: "Refer & Earn",
    path: "/refer-earn",
  },
  {
    label: "Withdraw Rules",
    path: "/withdraw-rules",
  },
  {
    label: "FAQ",
    path: "/faq",
  },
  {
    label: "Software",
    path: "/software",
  },
];

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Firebase auth state
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);

  /*
  ============================================================
  HIDE PUBLIC NAVBAR ON DASHBOARDS
  ============================================================
  */

  const isDashboardPage =
    location.pathname === "/dashboard" ||
    location.pathname.startsWith("/dashboard/");

  const isAdminPage =
    location.pathname === "/admin" ||
    location.pathname.startsWith("/admin/");

  const hideNavbar = isDashboardPage || isAdminPage;

  /*
  ============================================================
  FIREBASE AUTH STATE
  ============================================================
  */

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (!user) {
        setIsAdmin(false);
        setAuthLoading(false);
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnapshot = await getDoc(userRef);

        if (userSnapshot.exists()) {
          const userData = userSnapshot.data();

          setIsAdmin(userData?.role === "admin");
        } else {
          setIsAdmin(false);
        }
      } catch (error) {
        console.error("Navbar user lookup error:", error);
        setIsAdmin(false);
      } finally {
        setAuthLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  /*
  ============================================================
  SCROLL EFFECT
  ============================================================
  */

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 12);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /*
  ============================================================
  CLOSE MOBILE MENU ON ROUTE CHANGE
  ============================================================
  */

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  /*
  ============================================================
  ESCAPE KEY
  ============================================================
  */

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  /*
  ============================================================
  BODY SCROLL LOCK
  ============================================================
  */

  useEffect(() => {
    if (mobileMenuOpen && !hideNavbar) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen, hideNavbar]);

  /*
  ============================================================
  CLOSE MENU
  ============================================================
  */

  const closeMenu = () => {
    setMobileMenuOpen(false);
  };

  /*
  ============================================================
  LOGOUT
  ============================================================
  */

  const handleLogout = async () => {
    if (logoutLoading) return;

    setLogoutLoading(true);
    closeMenu();

    try {
      await signOut(auth);

      setCurrentUser(null);
      setIsAdmin(false);

      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      setLogoutLoading(false);
    }
  };

  /*
  ============================================================
  USER DISPLAY NAME
  ============================================================
  */

  const getUserInitial = () => {
    if (!currentUser) {
      return "U";
    }

    if (currentUser.displayName) {
      return currentUser.displayName
        .trim()
        .charAt(0)
        .toUpperCase();
    }

    if (currentUser.email) {
      return currentUser.email
        .trim()
        .charAt(0)
        .toUpperCase();
    }

    return "U";
  };

  /*
  ============================================================
  DON'T RENDER PUBLIC NAVBAR ON DASHBOARDS
  ============================================================
  */

  if (hideNavbar) {
    return null;
  }

  return (
    <>
      {/* =====================================================
          NAVBAR
      ===================================================== */}

      <header
        className={`fixed left-0 right-0 top-0 z-[100] border-b transition-all duration-300 ${
          scrolled
            ? "border-slate-200/80 bg-white/95 shadow-[0_8px_30px_rgba(15,23,42,0.07)] backdrop-blur-xl"
            : "border-slate-100/80 bg-white/90 backdrop-blur-md"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:h-[76px] sm:px-6 lg:px-8">

          {/* =================================================
              LOGO
          ================================================= */}

          <Link
            to="/"
            onClick={closeMenu}
            className="group flex shrink-0 items-center gap-3"
          >
            <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-[#05ad72] shadow-[0_8px_20px_rgba(5,173,114,0.20)] transition-all duration-300 group-hover:rotate-3 group-hover:scale-105 group-hover:shadow-[0_10px_25px_rgba(5,173,114,0.28)]">

              <MessageCircle
                size={21}
                strokeWidth={2.3}
                className="text-white"
              />

              <span className="absolute right-[5px] top-[5px] h-1.5 w-1.5 rounded-full bg-white" />

            </div>

            <div className="leading-none">
              <div className="text-[17px] font-extrabold tracking-[-0.04em] text-[#071a3b]">
                Refer<span className="text-[#05ad72]">x</span>
              </div>

              <div className="mt-1 text-[7px] font-bold uppercase tracking-[0.16em] text-slate-400">
                WhatsApp Automation
              </div>
            </div>
          </Link>

          {/* =================================================
              DESKTOP NAVIGATION
          ================================================= */}

          <nav className="hidden items-center gap-0.5 xl:flex">
            {navigationLinks.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                className={({ isActive }) =>
                  `group relative flex items-center rounded-lg px-3 py-2.5 text-[11px] font-bold transition-all duration-200 ${
                    isActive
                      ? "text-[#00a96d]"
                      : "text-slate-600 hover:bg-slate-50 hover:text-[#00a96d]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span>{item.label}</span>

                    <span
                      className={`absolute bottom-0 left-1/2 h-[2px] -translate-x-1/2 rounded-full bg-[#00a96d] transition-all duration-300 ${
                        isActive
                          ? "w-5 opacity-100"
                          : "w-0 opacity-0 group-hover:w-4 group-hover:opacity-70"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* =================================================
              DESKTOP ACCOUNT ACTIONS
          ================================================= */}

          <div className="hidden items-center gap-2 sm:flex">

            {/* =================================================
                AUTH LOADING
            ================================================= */}

            {authLoading ? (
              <div className="h-10 w-28 animate-pulse rounded-lg bg-slate-100" />
            ) : !currentUser ? (
              <>
                {/* LOGIN */}

                <Link
                  to="/login"
                  className="group flex items-center gap-1.5 rounded-lg px-3.5 py-2.5 text-[12px] font-bold text-slate-600 transition-all duration-200 hover:bg-slate-50 hover:text-[#00a96d]"
                >
                  <LogIn
                    size={14}
                    className="transition-transform duration-200 group-hover:-translate-x-0.5"
                  />

                  Login
                </Link>

                {/* GET STARTED */}

                <Link
                  to="/signup"
                  className="group flex items-center rounded-lg bg-[#00a96d] px-5 py-2.5 text-[12px] font-bold text-white shadow-[0_7px_18px_rgba(0,169,109,0.18)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#00945f] hover:shadow-[0_10px_25px_rgba(0,169,109,0.25)]"
                >
                  Get Started

                  <ArrowRight
                    size={14}
                    className="ml-1.5 transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Link>
              </>
            ) : (
              <>
                {/* USER / ADMIN INDICATOR */}

                <div className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-2.5 py-2">

                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-[10px] font-black text-emerald-700">
                    {getUserInitial()}
                  </div>

                  <div className="hidden 2xl:block max-w-[120px]">
                    <div className="truncate text-[10px] font-black text-slate-700">
                      {currentUser.displayName ||
                        currentUser.email?.split("@")[0] ||
                        "Account"}
                    </div>

                    <div className="text-[8px] font-bold uppercase tracking-wide text-slate-400">
                      {isAdmin ? "Administrator" : "Member"}
                    </div>
                  </div>

                </div>

                {/* DASHBOARD / ADMIN */}

                <Link
                  to={isAdmin ? "/admin" : "/dashboard"}
                  className="group flex items-center gap-1.5 rounded-lg px-3.5 py-2.5 text-[12px] font-bold text-slate-600 transition-all duration-200 hover:bg-slate-50 hover:text-[#00a96d]"
                >
                  {isAdmin ? (
                    <ShieldCheck
                      size={14}
                      className="transition-transform duration-200 group-hover:scale-105"
                    />
                  ) : (
                    <BarChart3
                      size={14}
                      className="transition-transform duration-200 group-hover:scale-105"
                    />
                  )}

                  {isAdmin ? "Admin Panel" : "Dashboard"}
                </Link>

                {/* LOGOUT */}

                <button
                  type="button"
                  onClick={handleLogout}
                  disabled={logoutLoading}
                  className="group flex items-center gap-1.5 rounded-lg px-3 py-2.5 text-[12px] font-bold text-slate-500 transition-all duration-200 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <LogOut
                    size={14}
                    className="transition-transform duration-200 group-hover:translate-x-0.5"
                  />

                  {logoutLoading ? "..." : "Logout"}
                </button>
              </>
            )}

          </div>

          {/* =================================================
              MOBILE MENU BUTTON
          ================================================= */}

          <button
            type="button"
            aria-label={
              mobileMenuOpen
                ? "Close navigation menu"
                : "Open navigation menu"
            }
            aria-expanded={mobileMenuOpen}
            onClick={() =>
              setMobileMenuOpen((previous) => !previous)
            }
            className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition-all duration-200 hover:border-emerald-200 hover:text-[#00a96d] sm:hidden"
          >

            {/* MENU */}

            <span
              className={`absolute transition-all duration-300 ${
                mobileMenuOpen
                  ? "rotate-90 scale-0 opacity-0"
                  : "rotate-0 scale-100 opacity-100"
              }`}
            >
              <Menu size={20} />
            </span>

            {/* CLOSE */}

            <span
              className={`absolute transition-all duration-300 ${
                mobileMenuOpen
                  ? "rotate-0 scale-100 opacity-100"
                  : "-rotate-90 scale-0 opacity-0"
              }`}
            >
              <X size={20} />
            </span>

          </button>
        </div>

        {/* =================================================
            MOBILE OVERLAY
        ================================================= */}

        <div
          onClick={closeMenu}
          className={`fixed inset-0 top-[72px] bg-slate-900/20 backdrop-blur-[2px] transition-all duration-300 sm:hidden ${
            mobileMenuOpen
              ? "pointer-events-auto visible opacity-100"
              : "pointer-events-none invisible opacity-0"
          }`}
        />

        {/* =================================================
            MOBILE MENU
        ================================================= */}

        <div
          className={`absolute left-0 right-0 top-full overflow-hidden border-t border-slate-100 bg-white shadow-[0_25px_50px_rgba(15,23,42,0.12)] transition-all duration-300 sm:hidden ${
            mobileMenuOpen
              ? "visible max-h-[calc(100vh-72px)] translate-y-0 opacity-100"
              : "invisible max-h-0 -translate-y-2 opacity-0"
          }`}
        >
          <div className="max-h-[calc(100vh-72px)] overflow-y-auto px-5 pb-6 pt-4">

            {/* =================================================
                USER INFO
            ================================================= */}

            {!authLoading && currentUser && (
              <div className="mb-4 flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-3">

                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-black text-emerald-700">
                  {getUserInitial()}
                </div>

                <div className="min-w-0">
                  <div className="truncate text-sm font-black text-slate-800">
                    {currentUser.displayName ||
                      currentUser.email?.split("@")[0] ||
                      "Account"}
                  </div>

                  <div className="truncate text-[10px] font-semibold text-slate-400">
                    {currentUser.email}
                  </div>

                  <div className="mt-0.5 text-[9px] font-black uppercase tracking-wide text-emerald-600">
                    {isAdmin ? "Administrator" : "Refrex Member"}
                  </div>
                </div>

              </div>
            )}

            {/* =================================================
                PUBLIC NAVIGATION
            ================================================= */}

            <nav className="space-y-1">
              {navigationLinks.map((item, index) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/"}
                  onClick={closeMenu}
                  className={({ isActive }) =>
                    `group flex items-center justify-between rounded-xl px-4 py-3.5 transition-all duration-200 ${
                      isActive
                        ? "bg-emerald-50 text-[#00a96d]"
                        : "text-slate-600 hover:bg-slate-50 hover:text-[#00a96d]"
                    }`
                  }
                  style={{
                    transitionDelay: mobileMenuOpen
                      ? `${index * 35}ms`
                      : "0ms",
                  }}
                >
                  {({ isActive }) => (
                    <>
                      <div className="flex items-center gap-3">

                        <span
                          className={`flex h-7 w-7 items-center justify-center rounded-lg text-[9px] font-black ${
                            isActive
                              ? "bg-emerald-100 text-emerald-600"
                              : "bg-slate-100 text-slate-400"
                          }`}
                        >
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span className="text-sm font-bold">
                          {item.label}
                        </span>

                      </div>

                      <ChevronRight
                        size={17}
                        className={`transition-all duration-200 ${
                          isActive
                            ? "translate-x-0.5 text-emerald-500"
                            : "text-slate-300 group-hover:translate-x-0.5 group-hover:text-emerald-500"
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            {/* =================================================
                MOBILE DASHBOARD / ADMIN
            ================================================= */}

            {!authLoading && currentUser && (
              <div className="mt-2">

                <NavLink
                  to={isAdmin ? "/admin" : "/dashboard"}
                  onClick={closeMenu}
                  className={({ isActive }) =>
                    `group flex items-center justify-between rounded-xl px-4 py-3.5 transition-all duration-200 ${
                      isActive
                        ? "bg-emerald-50 text-[#00a96d]"
                        : "text-slate-600 hover:bg-slate-50 hover:text-[#00a96d]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <div className="flex items-center gap-3">

                        <span
                          className={`flex h-7 w-7 items-center justify-center rounded-lg ${
                            isActive
                              ? "bg-emerald-100 text-emerald-600"
                              : "bg-slate-100 text-slate-400"
                          }`}
                        >
                          {isAdmin ? (
                            <ShieldCheck size={14} />
                          ) : (
                            <BarChart3 size={14} />
                          )}
                        </span>

                        <span className="text-sm font-bold">
                          {isAdmin
                            ? "Admin Panel"
                            : "Dashboard"}
                        </span>

                      </div>

                      <ChevronRight
                        size={17}
                        className="text-slate-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-emerald-500"
                      />
                    </>
                  )}
                </NavLink>

              </div>
            )}

            {/* =================================================
                DIVIDER
            ================================================= */}

            <div className="my-4 h-px bg-slate-100" />

            {/* =================================================
                MOBILE ACCOUNT ACTIONS
            ================================================= */}

            {authLoading ? (
              <div className="h-12 animate-pulse rounded-xl bg-slate-100" />
            ) : !currentUser ? (
              <div className="grid grid-cols-2 gap-3">

                <Link
                  to="/login"
                  onClick={closeMenu}
                  className="flex h-12 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-sm font-bold text-slate-700 transition-all duration-200 hover:border-emerald-200 hover:text-emerald-600"
                >
                  <LogIn size={15} />

                  Login
                </Link>

                <Link
                  to="/signup"
                  onClick={closeMenu}
                  className="group flex h-12 items-center justify-center rounded-xl bg-[#00a96d] text-sm font-bold text-white shadow-lg shadow-emerald-500/15 transition-all duration-300 hover:bg-[#00945f]"
                >
                  Get Started

                  <ArrowRight
                    size={15}
                    className="ml-1.5 transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Link>

              </div>
            ) : (
              <button
                type="button"
                onClick={handleLogout}
                disabled={logoutLoading}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-rose-100 bg-rose-50 text-sm font-black text-rose-600 transition-all duration-200 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <LogOut size={16} />

                {logoutLoading
                  ? "Logging out..."
                  : "Logout"}
              </button>
            )}

            {/* =================================================
                MOBILE STATUS
            ================================================= */}

<div className="mt-5 flex items-center justify-center gap-2 text-[9px] font-semibold"> 
  <span className="relative flex h-2 w-2"> 
    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" /> 
    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /> 
  </span> 

  <span>
    <span className="text-emerald-600">Refer</span>
    <span className="text-black">x WhatsApp Automation</span>
  </span>
</div>

          </div>
        </div>
      </header>

      {/* =====================================================
          NAVBAR SPACER
      ===================================================== */}

      <div className="h-[72px] sm:h-[76px]" />
    </>
  );
}

export default Navbar;